/**
 * Coordinate one long-running operation per project. A second caller for the
 * same project joins the existing promise; a different project is refused
 * until the first operation settles. This is an execution lock, not UI state.
 */
export function createProjectSingleflight(label = 'Operation') {
    let active: { projectId: string; promise: Promise<any>; token: symbol } | null = null;
    const queuedAfterActive = new Map<string, Promise<any>>();

    const run = (projectId: any, operation: any) => {
        if (typeof operation !== 'function') {
            return Promise.reject(new TypeError(`${label} singleflight requires an operation`));
        }

        const key = String(projectId || 'unknown-project');
        if (active) {
            if (active.projectId === key) return active.promise;
            const error = new Error(
                `${label} is already running for project ${active.projectId}`,
            );
            (error as any).code = 'PROJECT_OPERATION_IN_FLIGHT';
            (error as any).retryable = true;
            return Promise.reject(error);
        }

        const token = Symbol(label);
        const promise = Promise.resolve()
            .then(operation)
            .finally(() => {
                if (active?.token === token) active = null;
            });
        active = { projectId: key, promise, token };
        return promise;
    };

    return {
        run,

        /**
         * Wait for the operation that currently owns the lock, then revalidate
         * before attempting the requested key. This is deliberately separate
         * from run(): ordinary cross-project callers must still fail fast.
         *
         * Concurrent queued callers for the same key remain single-flighted:
         * after the prior owner settles, the first starts and the rest join it.
         */
        runAfterActive(projectId: any, operation: any, shouldRun: any = () => true) {
            if (typeof operation !== 'function') {
                return Promise.reject(new TypeError(`${label} singleflight requires an operation`));
            }
            if (typeof shouldRun !== 'function') {
                return Promise.reject(new TypeError(`${label} queued singleflight requires a revalidation function`));
            }

            const key = String(projectId || 'unknown-project');
            const existingQueued = queuedAfterActive.get(key);
            if (existingQueued) return existingQueued;

            const prior = active?.promise || null;
            const queued: Promise<any> = (async () => {
                if (prior) {
                    try { await prior; } catch { /* the replacement owns its own outcome */ }
                }
                if (!await shouldRun()) return null;
                return run(key, operation);
            })().finally(() => {
                if (queuedAfterActive.get(key) === queued) queuedAfterActive.delete(key);
            });
            queuedAfterActive.set(key, queued);
            return queued;
        },

        /**
         * Like runAfterActive, but every caller keeps its own place: it never
         * joins another caller's queued or running operation, even for the
         * same key. It waits until no operation holds the lock, asks shouldRun
         * again, and only then starts its own. For a request whose result must
         * be its own: DEC-098's background quote author must never take a
         * stale author's result for a newer draft.
         */
        async runOwnAfterActive(projectId: any, operation: any, shouldRun: any = () => true) {
            if (typeof operation !== 'function') {
                throw new TypeError(`${label} singleflight requires an operation`);
            }
            if (typeof shouldRun !== 'function') {
                throw new TypeError(`${label} queued singleflight requires a revalidation function`);
            }
            const key = String(projectId || 'unknown-project');
            for (;;) {
                while (active) {
                    const prior = active.promise;
                    try { await prior; } catch { /* the earlier owner's outcome is its own */ }
                }
                if (!await shouldRun()) return null;
                // shouldRun may await: start only if nothing claimed the lock meanwhile.
                if (!active) return run(key, operation);
            }
        },

        status() {
            return active ? { active: true, projectId: active.projectId } : { active: false, projectId: null };
        },

        /** Resolves once the operation holding the lock now has settled (at once when idle). */
        settled(): Promise<void> {
            return active ? active.promise.then(() => undefined, () => undefined) : Promise.resolve();
        },
    };
}

/**
 * Acquire one synchronous project-write lease before an operation performs any
 * awaited side effect. The returned lease remains the authority across those
 * awaits and revalidates both its token and immutable project owner before the
 * caller consumes an awaited result.
 *
 * This deliberately accepts a React-style `{ current }` ref rather than owning
 * hidden module state: App keeps the lease scoped to the mounted QuoteMaker
 * session, while the token-safe release prevents an old finally block from
 * clearing a newer save.
 */
export function acquireProjectWriteLease({
    leaseRef,
    owner = null,
    isOwnerCurrent = () => true,
    isConflictingOperationActive = () => false,
    onRelease = null,
    label = 'Project write',
}: {
    leaseRef?: { current: any } | null;
    owner?: any;
    isOwnerCurrent?: (owner: any) => boolean;
    isConflictingOperationActive?: () => boolean;
    onRelease?: (() => void) | null;
    label?: string;
} = {}) {
    if (!leaseRef || typeof leaseRef !== 'object') {
        throw new TypeError(`${label} lease requires a mutable ref`);
    }
    if (leaseRef.current || isConflictingOperationActive()) return null;

    const token = Symbol(label);
    let released = false;
    const staleError = () => {
        const error = new Error(`${label} no longer owns the active project.`) as Error & {
            code?: string;
            retryable?: boolean;
        };
        error.code = 'PROJECT_WRITE_LEASE_STALE';
        error.retryable = true;
        return error;
    };
    const isCurrent = () => (
        released !== true
        && leaseRef.current?.token === token
        && isOwnerCurrent(owner) === true
        && isConflictingOperationActive() !== true
    );
    const assertCurrent = () => {
        if (!isCurrent()) throw staleError();
        return true;
    };
    const lease = {
        token,
        owner,
        isCurrent,
        assertCurrent,
        async awaitOwned<T>(promise: Promise<T> | T): Promise<T> {
            assertCurrent();
            const result = await promise;
            assertCurrent();
            return result;
        },
        release() {
            if (released) return false;
            released = true;
            if (leaseRef.current?.token !== token) return false;
            leaseRef.current = null;
            if (typeof onRelease === 'function') onRelease();
            return true;
        },
    };
    leaseRef.current = lease;
    return lease;
}

/**
 * Resolve whether the quote execution lock owns either the project currently
 * painted on screen or the same project's just-claimed load generation. A
 * project-open increments the request generation before the old screen is
 * replaced, so consulting only the request refs can briefly miss the active
 * owner and allow the stale Stage 5 editor to write back.
 */
export function isSingleflightActiveForPaintedProject({
    status = null,
    paintedContext = null,
    currentProjectId = null,
    currentLoadGeneration = null,
}: {
    status?: { active?: boolean; projectId?: string | null } | null;
    paintedContext?: { projectId?: unknown; loadGeneration?: unknown } | null;
    currentProjectId?: unknown;
    currentLoadGeneration?: unknown;
} = {}) {
    if (status?.active !== true || !status.projectId) return false;
    const ownerCandidates = new Set<string>();
    const paintedProjectId = paintedContext?.projectId;
    const paintedGeneration = Number(paintedContext?.loadGeneration);
    if (paintedProjectId != null && Number.isFinite(paintedGeneration)) {
        ownerCandidates.add(`${paintedProjectId || 'unsaved-project'}:${paintedGeneration}`);
    }
    const currentGeneration = Number(currentLoadGeneration);
    if (currentProjectId != null && Number.isFinite(currentGeneration)) {
        ownerCandidates.add(`${currentProjectId || 'unsaved-project'}:${currentGeneration}`);
    }
    return ownerCandidates.has(String(status.projectId));
}
